import apiClient, { unwrapData } from "./apiClient";
import { filterDashboardCalls, mapHistoryCallToDashboardCall } from "./liveDataAdapters";
import { PAGE_SIZE } from "../utils/constants";
import { formatDuration } from "../utils/formatters";

const DASHBOARD_CALL_LIMIT = 1000;

function buildPerformanceFromAggregates(items = [], stats = {}) {
  const chartData = items
    .map((item) => {
      const dateKey = String(item.call_date || "").slice(0, 10);
      const parsedDate = dateKey ? new Date(`${dateKey}T00:00:00`) : null;
      const label = parsedDate && !Number.isNaN(parsedDate.getTime())
        ? parsedDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })
        : dateKey || "--";

      return {
        label,
        date: dateKey,
        totalCalls: Number(item.totalCalls || 0),
        answeredCalls: Number(item.answeredCalls ?? item.completedCalls ?? 0),
        inboundCalls: Number(item.inboundCalls || 0),
        outboundCalls: Number(item.outboundCalls || 0),
        droppedCalls: Number(item.droppedCalls || 0),
      };
    })
    .filter((item) => item.date);

  const avgDurationSeconds = Number(stats.avg_talk_time_seconds || stats.avg_duration || 0);
  const avgWaitSeconds = Number(stats.avg_wait_time_seconds || 0);
  const answeredCalls = Number(stats.answered_calls || 0);
  const answerRate = Number(stats.answer_rate || 0);

  return {
    chartData,
    metrics: [
      {
        key: "talk-time",
        label: "AVG TALK TIME",
        value: avgDurationSeconds ? formatDuration(avgDurationSeconds) : "--",
      },
      {
        key: "wait-time",
        label: "AVG WAIT TIME",
        value: avgWaitSeconds ? formatDuration(avgWaitSeconds) : "--",
      },
      {
        key: "answered-calls",
        label: "ANSWERED CALLS",
        value: answeredCalls ? `${answeredCalls.toLocaleString()} (${answerRate.toFixed(1)}%)` : "--",
      },
      {
        key: "top-agent",
        label: "MOST ACTIVE AGENT",
        value: stats.top_agent_name || "--",
      },
      {
        key: "top-line",
        label: "MOST USED NUMBER / LINE",
        value: stats.top_line_label || "--",
      },
    ],
  };
}

async function loadDashboardCalls(options = {}) {
  const response = await apiClient.get("/api/calls/history", {
    ...options,
    query: {
      ...(options.query || {}),
      view: "canonical",
      limit: options.query?.limit || DASHBOARD_CALL_LIMIT,
      page: options.query?.page || 1,
    },
  });

  const items = unwrapData(response)?.items || [];
  return items.map(mapHistoryCallToDashboardCall);
}

export async function getDashboardStats(options = {}) {
  const { signal, query = {} } = options;
  const requestOptions = {
    ...(signal ? { signal } : {}),
    query,
  };

  const [statsResponse, performanceResponse] = await Promise.all([
    apiClient.get("/api/stats", requestOptions),
    apiClient.get("/api/dashboard/call-performance", requestOptions),
  ]);

  const stats = unwrapData(statsResponse) || {};
  const performanceData = unwrapData(performanceResponse) || {};
  const totalCalls = Number(stats.total_calls || 0);
  const totalBookings = Number(stats.total_bookings || 0);
  const bookingRate = totalCalls > 0
    ? Number(((totalBookings / totalCalls) * 100).toFixed(2))
    : 0;

  return {
    ...stats,
    booking_rate: bookingRate,
    performance: buildPerformanceFromAggregates(
      Array.isArray(performanceData.items) ? performanceData.items : [],
      stats,
    ),
  };
}

export async function getDashboardCalls(params = {}, options = {}) {
  const allCalls = await loadDashboardCalls({
    ...options,
    query: {
      limit: DASHBOARD_CALL_LIMIT,
      page: 1,
      search: params.search,
      direction: params.direction,
      line: params.line,
      duration_min: params.duration_min,
      duration_max: params.duration_max,
      date_from: params.date_from,
      date_to: params.date_to,
    },
  });
  const filteredCalls = filterDashboardCalls(allCalls, params);
  const currentPage = Number(params.offset || 0) / PAGE_SIZE;
  const startIndex = Number(params.offset || 0);
  const endIndex = startIndex + Number(params.limit || PAGE_SIZE);

  return {
    calls: filteredCalls.slice(startIndex, endIndex),
    total: filteredCalls.length,
    page: Number.isFinite(currentPage) ? currentPage + 1 : 1,
  };
}
